import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

/**
 * Utility function to merge Tailwind CSS classes
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Formats a number as Brazilian currency (BRL)
 */
export function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return "R$ 0,00";
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
}

/**
 * Parses a formatted Brazilian currency string back to a number
 */
export function parseFormattedCurrency(value: string): number {
  // Remove currency symbol, dots and replace comma with dot
  const numericString = value
    .replace(/[R$\s.]/g, '')
    .replace(',', '.');
  return parseFloat(numericString) || 0;
}

/**
 * Formats a date to Brazilian format (dd/mm/yyyy) correctly handling timezones.
 * If the input is a date-only string (YYYY-MM-DD), it preserves the date.
 */
export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return '';

  let d: Date;
  if (typeof date === 'string') {
    const dateOnly = extractDateOnly(date);
    if (dateOnly) {
      const [y, m, d] = dateOnly.split('-');
      return `${d}/${m}/${y}`;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      d = new Date(date.replace(/-/g, '/'));
    } else {
      d = new Date(date);
    }
  } else {
    d = date;
  }

  return d.toLocaleDateString('pt-BR');
}

const TZ_BRASIL = 'America/Sao_Paulo';

/** Extrai YYYY-MM-DD de strings date ou timestamptz */
export function extractDateOnly(value: string): string | null {
  const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
}

/**
 * Formata data no fuso America/Sao_Paulo (UTC-3).
 * Valores date-only (YYYY-MM-DD ou ISO com hora) preservam o dia civil.
 */
export function formatDateUTC3(date: string | Date | null | undefined): string {
  if (!date) return '';

  if (typeof date === 'string') {
    const dateOnly = extractDateOnly(date);
    if (dateOnly) {
      const [y, m, d] = dateOnly.split('-');
      return `${d}/${m}/${y}`;
    }
  }

  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return '';

  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: TZ_BRASIL,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d);
}

/**
 * Formata data e hora no fuso America/Sao_Paulo (UTC-3).
 */
export function formatDateTimeUTC3(date: string | Date | null | undefined): string {
  if (!date) return '';

  if (typeof date === 'string') {
    const dateOnly = extractDateOnly(date);
    // Timestamptz com meia-noite UTC costuma ser coluna date serializada — só a data
    if (dateOnly && /T00:00:00(\.0+)?(Z|[+-]\d{2}:\d{2})?$/.test(date)) {
      return formatDateUTC3(dateOnly);
    }
  }

  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return '';

  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: TZ_BRASIL,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

/**
 * Combina data + hora de agendamento como horário local (sem shift UTC).
 */
export function parseAgendamentoTimestamp(
  dataAgendamento: string,
  horaAgendamento?: string | null
): Date {
  const dateOnly = extractDateOnly(dataAgendamento);
  const datePart = (dateOnly ?? dataAgendamento).replace(/-/g, '/');
  if (horaAgendamento) {
    const parts = horaAgendamento.split(':');
    const h = parts[0] || '00';
    const m = parts[1] || '00';
    const s = parts[2] || '00';
    return new Date(`${datePart} ${h}:${m}:${s}`);
  }
  return new Date(datePart);
}

/**
 * Formats a date to Brazilian format with time (dd/mm/yyyy HH:mm)
 */
export function formatDateTime(date: string | Date | null | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleString('pt-BR');
}

/**
 * Formats a number as percentage
 */
export function formatPercent(value: number | null | undefined, decimals: number = 1): string {
  if (value === null || value === undefined) return "0%";
  return `${value.toFixed(decimals)}%`;
}

/**
 * Formats a number with thousand separators
 */
export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return "0";
  return new Intl.NumberFormat('pt-BR').format(value);
}

/**
 * Formats a phone number to Brazilian format
 */
export function formatPhone(phone: string): string {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 11) {
    return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  if (cleaned.length === 10) {
    return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  return phone;
}

/**
 * Formats a CPF number to Brazilian format
 */
export function formatCPF(cpf: string): string {
  if (!cpf) return '';
  const cleaned = cpf.replace(/\D/g, '');
  return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

/**
 * Formats a CNPJ number to Brazilian format
 */
export function formatCNPJ(cnpj: string): string {
  if (!cnpj) return '';
  const cleaned = cnpj.replace(/\D/g, '');
  return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
}

/**
 * Formats a CEP number to Brazilian format
 */
export function formatCEP(cep: string): string {
  if (!cep) return '';
  const cleaned = cep.replace(/\D/g, '');
  return cleaned.replace(/(\d{5})(\d{3})/, '$1-$2');
}

/**
 * Debounces a function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Truncates a string to a maximum length
 */
export function truncate(str: string, length: number): string {
  if (!str) return '';
  if (str.length <= length) return str;
  return `${str.slice(0, length)}...`;
}

/**
 * Removes accents from a string
 */
export function removeAccents(str: string): string {
  if (!str) return '';
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/**
 * Capitalizes the first letter of each word in a string
 */
export function capitalize(str: string): string {
  if (!str) return '';
  return str.toLowerCase().replace(/(?:^|\s)\S/g, a => a.toUpperCase());
}

/**
 * Checks if a value is empty (null, undefined, empty string, empty array, or empty object)
 */
export function isEmpty(value: any): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

/**
 * Copies a string to the clipboard with a fallback for older browsers or non-secure contexts
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback using document.execCommand('copy')
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      textArea.style.top = "0";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      return successful;
    }
  } catch (err) {
    console.error('Error copying to clipboard:', err);
    return false;
  }
}

/**
 * Gets the current date/time adjusted to UTC-3 (Brazil/Sao Paulo)
 */
export function getNowUTC3(): Date {
  const now = new Date();
  // Get UTC time in milliseconds
  const utcTime = now.getTime() + (now.getTimezoneOffset() * 60000);
  // Brazil is UTC-3 (fixed -180 minutes)
  return new Date(utcTime - (180 * 60000));
}

/**
 * Returns a date object at the beginning of the day in UTC-3
 */
export function startOfDayUTC3(date: Date = getNowUTC3()): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Checks if a proposal is expired based on UTC-3 time.
 * A proposal is expired if today (UTC-3) is strictly after the expiration date.
 */
export function isPropostaExpirada(dataValidade: Date | string | null | undefined): boolean {
  if (!dataValidade) return false;

  // Se for string do Supabase (tipo date), virá como 'YYYY-MM-DD'
  // new Date('2026-03-10') cria 2026-03-10 00:00:00 UTC
  const validade = typeof dataValidade === 'string' ? new Date(dataValidade) : dataValidade;
  const hoje = getNowUTC3();

  const hDate = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());

  // Se a data de validade veio do banco como 'date', as propriedades UTC representam a data real
  // Ex: '2026-03-10' -> getUTCFullYear=2026, getUTCMonth=2, getUTCDate=10
  const vDate = new Date(validade.getUTCFullYear(), validade.getUTCMonth(), validade.getUTCDate());

  return hDate > vDate;
}

export {
  formatStatusLabel,
  formatContratoStatusLabel,
  formatAgendamentoStatusLabel,
  humanizeStatus,
  normalizeStatusKey,
  STATUS_LABELS,
} from './statusLabels';

export {
  formatTempoProcuraLabel,
  TEMPO_PROCURA_LABELS,
  TEMPO_PROCURA_OPTIONS,
} from './qualificacaoLabels';
